import React, { useState } from "react";
import { Phone, MapPin, Mail, Send, MessageCircle } from "lucide-react";
import { CONTACT, PACKAGES } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";

const BookingForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    pkg: "",
    date: "",
    message: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({
        title: "Missing details",
        description: "Please enter your name and phone number.",
      });
      return;
    }
    // Frontend-only: save to localStorage (MOCK, no backend yet)
    const prev = JSON.parse(localStorage.getItem("enquiries") || "[]");
    localStorage.setItem(
      "enquiries",
      JSON.stringify([...prev, { ...form, at: new Date().toISOString() }])
    );
    toast({
      title: "Enquiry received! \uD83D\uDE4F",
      description: "Our team will call you shortly to confirm your booking.",
    });
    // Also open WhatsApp with prefilled message
    const msg = encodeURIComponent(
      `Booking Enquiry%0AName: ${form.name}%0APhone: ${form.phone}%0APackage: ${
        form.pkg || "-"
      }%0ADate: ${form.date || "-"}%0AMessage: ${form.message || "-"}`
    );
    window.open(`https://wa.me/${CONTACT.whatsapp}?text=${msg}`, "_blank");
    setForm({ name: "", phone: "", pkg: "", date: "", message: "" });
  };

  return (
    <section id="contact" className="py-20 bg-[#fffaf3]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 items-stretch">
          {/* Info */}
          <div className="rounded-3xl bg-gradient-to-br from-orange-700 to-orange-900 text-white p-8 sm:p-10 shadow-xl">
            <span className="text-amber-300 font-medium tracking-widest uppercase text-sm">
              Get In Touch
            </span>
            <h2 className="font-display text-3xl sm:text-4xl mt-2 mb-4">
              Book Your Srisailam Trip Today
            </h2>
            <p className="text-orange-100/90 mb-8">
              Call us or fill in the form and our team will get back to you with
              the best all-inclusive package for your pilgrimage.
            </p>

            <div className="space-y-5">
              <a href={`tel:+91${CONTACT.primary}`} className="flex items-center gap-4 group">
                <span className="h-12 w-12 rounded-2xl bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs text-orange-200 uppercase tracking-wide">Call / WhatsApp</div>
                  <div className="text-lg font-semibold">{CONTACT.primary}</div>
                </div>
              </a>
              <a href={`tel:+91${CONTACT.secondary}`} className="flex items-center gap-4 group">
                <span className="h-12 w-12 rounded-2xl bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs text-orange-200 uppercase tracking-wide">Secondary</div>
                  <div className="text-lg font-semibold">{CONTACT.secondary}</div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <span className="h-12 w-12 rounded-2xl bg-white/15 flex items-center justify-center">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs text-orange-200 uppercase tracking-wide">Address</div>
                  <div className="text-sm font-medium max-w-xs">{CONTACT.address}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl bg-white p-8 sm:p-10 shadow-xl border border-amber-100"
          >
            <h3 className="font-display text-2xl text-orange-900 mb-6">
              Quick Enquiry
            </h3>
            <div className="space-y-4">
              <Input
                placeholder="Your Name*"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
              />
              <Input
                placeholder="Phone Number*"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
              />
              <Select value={form.pkg} onValueChange={(v) => update("pkg", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Package" />
                </SelectTrigger>
                <SelectContent>
                  {PACKAGES.map((p) => (
                    <SelectItem key={p.id} value={p.title}>
                      {p.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => update("date", e.target.value)}
              />
              <Textarea
                placeholder="Message (optional)"
                rows={3}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
              />
              <Button
                type="submit"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-full gap-2 h-11"
              >
                <Send className="h-4 w-4" /> Send Enquiry
              </Button>
              <p className="text-center text-xs text-stone-400 flex items-center justify-center gap-1">
                <MessageCircle className="h-3 w-3" /> Also opens WhatsApp with your details
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default BookingForm;
